import httpService from '@/core/http/http.service';
import { createUrlFactory } from '@/project/http/ApiHelper.service';
import {
    AdContentBlocksApiRequest,
    AdContentBlockViewModel,
    AdnuntiusPixelForwardRequest,
    BasePersonalizedContentApiRequest,
    BasketPageApiRequest,
    BrandLogosApiRequest,
    BrandLogoViewModel,
    CategoryItemViewModel,
    PartnerLogosApiRequest,
    PartnerLogoViewModel,
    PreviewPersonalizedContentViewModel,
    ProductDetailsPageAdRequest,
    ProductListApiRequest,
    RequestBreakpoint
} from '@/types/apiServerContract';
import myAccountStore from '@/project/app/client-pages/my-account/my-account.store';
import { bannerPreviewType } from '@/project/app/client-pages/my-account/banner-preview.type';

export class PersonalizationController {
    private readonly urlFactory = createUrlFactory('Personalization');

    public async personalizedContent(adUnitIds: string[], currentUrl: string, contactKey: string, customerKey?: string): Promise<AdContentBlockViewModel[]> {
        if (myAccountStore.adnuntiusPreview) {
            console.log('Previewing banner', { previewState: myAccountStore.adnuntiusPreview });
            const response: AdContentBlockViewModel[] = [];
            const bannerResponse = await this.previewBanner();
            adUnitIds.forEach(adId => {
                response.push({ ...bannerResponse?.adContentBlockViewModel ?? {}, adUnitId: adId } as unknown as AdContentBlockViewModel);
            });
            return response;
        } else {
            return await httpService.post(this.urlFactory('PersonalizedContent'), {
                adUnitIds,
                currentUrl,
                contactKey,
                customerKey
            } as AdContentBlocksApiRequest);
        }
    }

    public async previewBanner() {
        let bannerResponse: PreviewPersonalizedContentViewModel | undefined;
        try {
            bannerResponse = await httpService.get(this.urlFactory('PreviewPersonalizedContent'), {
                    creativeId: myAccountStore.adnuntiusPreview?.creativeId,
                    languageCode: myAccountStore.adnuntiusPreview?.language
                },
                undefined,
                undefined,
                'bannerPreview');
            if (myAccountStore.adnuntiusPreview
                && (
                    myAccountStore.adnuntiusPreview.bannerType === bannerPreviewType.PRODUCT_LIST
                    || myAccountStore.adnuntiusPreview.bannerType === bannerPreviewType.PRODUCT_DETAILS
                    || myAccountStore.adnuntiusPreview.bannerType === bannerPreviewType.IMAGE_HALF_WIDTH
                    || myAccountStore.adnuntiusPreview.bannerType === bannerPreviewType.IMAGE_FULL_WIDTH
                )) {
                // Fix that some banner types have "backgroundImages" array and some only have a single "backgroundImage"
                // @ts-ignore
                bannerResponse.adContentBlockViewModel.backgroundImage = bannerResponse.adContentBlockViewModel.backgroundImages[0] || { };
            }
            if (myAccountStore.adnuntiusPreview) {
                myAccountStore.adnuntiusPreview.availableLanguages = bannerResponse?.languageCodes || [];
            }
        } catch (error) {
            if (myAccountStore.adnuntiusPreview) {
                myAccountStore.adnuntiusPreview.loadError = true;
            }
        } finally {
            if (myAccountStore.adnuntiusPreview) {
                myAccountStore.adnuntiusPreview.loading = false;
            }
        }
        return bannerResponse;
    }

    public async categories(adUnitId: string, currentUrl: string, contactKey: string, customerKey?: string): Promise<CategoryItemViewModel[]> {
        return httpService.post(this.urlFactory('Categories'), {
            adUnitIds: [adUnitId],
            currentUrl,
            contactKey,
            customerKey
        } as AdContentBlocksApiRequest);
    }

    public async brandLogos(breakpoint: RequestBreakpoint, currentUrl: string, contactKey: string, customerKey?: string): Promise<BrandLogoViewModel[]> {
        return httpService.post(this.urlFactory('BrandLogos'), {
            breakpoint,
            currentUrl,
            contactKey,
            customerKey
        } as BrandLogosApiRequest);
    }

    public async recommendedBrands(brands: string[], currentUrl: string, contactKey: string, customerKey?: string): Promise<BrandLogoViewModel[]> {
        return httpService.post(this.urlFactory('RecommendedBrands'), {
            brands,
            currentUrl,
            contactKey,
            customerKey
        } as BrandLogosApiRequest);
    }

    public async partnerLogos(brandName: string, condition: string, department: string, mpnNumber: string, itemNo: string, pph: string, productCategory: string,
        productGroup: string, currentUrl: string, contactKey: string, customerKey?: string): Promise<PartnerLogoViewModel[]> {
        return httpService.post(this.urlFactory('PartnerLogos'), {
            brandName,
            condition,
            department,
            mpnNumber,
            itemNo,
            pph,
            productCategory,
            productGroup,
            currentUrl,
            contactKey,
            customerKey
        } as PartnerLogosApiRequest);
    }

    public async productListBanner(request: ProductListApiRequest): Promise<AdContentBlockViewModel> {
        return this.getBanner(request, 'ProductListBanner');
    }

    public async basketPageBanner(request: BasketPageApiRequest): Promise<AdContentBlockViewModel> {
        return this.getBanner(request, 'BasketPageBanner');
    }

    public async productDetailBanner(request: ProductDetailsPageAdRequest): Promise<AdContentBlockViewModel> {
        return this.getBanner(request, 'ProductDetailsPageBanner');
    }

    public async forwardAdnuntiusPixelRequest(requests: AdnuntiusPixelForwardRequest): Promise<void> {
        return httpService.post(this.urlFactory('forwardAdnuntiusPixelRequest'), requests);
    }

    private async getBanner(request: BasePersonalizedContentApiRequest, factoryRequest: string): Promise<AdContentBlockViewModel> {
        if (myAccountStore.adnuntiusPreview) {
            const bannerResponse = await this.previewBanner();
            // @ts-ignore
            return {
                ...bannerResponse?.adContentBlockViewModel ?? {},
            };
        }

        return httpService.post(this.urlFactory(factoryRequest), request);
    }
}
